// Schematic positions for things on the hologram ring (DESIGN.md v5 mockup).
// Cameras sit on the outer ring, buttons on the middle ring, drones on the
// inner ring. Slots are spread over a top arc and a bottom arc so no callout
// lands at 3 or 9 o'clock, where the camera and alerts panels float. Cameras
// take the slots furthest left (short tether to the camera panel), buttons the
// slots furthest right (next to the alerts), drones what is left.

export type RingKind = "cam" | "btn" | "drone";

export const RING_R: Record<RingKind, number> = { cam: 250, btn: 200, drone: 120 };

/** The SVG viewBox is 0 0 560 560 with the center at 280, 280. */
export const C = 280;

const ARCS: Array<[number, number]> = [
  [-64, 64],
  [116, 244],
];

/** Angles in degrees, clockwise from 12 o'clock, for n slots. */
export function slotAngles(n: number): number[] {
  if (n <= 0) return [];
  const top = Math.ceil(n / 2);
  const counts = [top, n - top];
  const out: number[] = [];
  ARCS.forEach(([a0, a1], ai) => {
    const k = counts[ai] ?? 0;
    for (let i = 0; i < k; i++) out.push(a0 + ((a1 - a0) * (i + 0.5)) / k);
  });
  return out;
}

export interface Placed<T> {
  item: T;
  kind: RingKind;
  angle: number;
  r: number;
  x: number;
  y: number;
}

export function polar(r: number, deg: number): { x: number; y: number } {
  const t = (deg * Math.PI) / 180;
  return { x: C + r * Math.sin(t), y: C - r * Math.cos(t) };
}

/**
 * Assign every item a slot. Items keep their order within a kind, so the
 * first camera always gets the upper-left slot.
 */
export function layoutRing<T>(groups: { cam: T[]; btn: T[]; drone: T[] }): Placed<T>[] {
  const n = groups.cam.length + groups.btn.length + groups.drone.length;
  const free = slotAngles(n).map((a) => ({ a, s: Math.sin((a * Math.PI) / 180), c: Math.cos((a * Math.PI) / 180) }));
  const out: Placed<T>[] = [];
  const take = (kind: RingKind, items: T[], score: (s: number, c: number) => number) => {
    for (const item of items) {
      let best = -1;
      for (let i = 0; i < free.length; i++) {
        const f = free[i]!;
        const b = best === -1 ? null : free[best]!;
        if (!b || score(f.s, f.c) < score(b.s, b.c) - 1e-9) best = i;
      }
      if (best === -1) return;
      const slot = free.splice(best, 1)[0]!;
      const r = RING_R[kind];
      out.push({ item, kind, angle: slot.a, r, ...polar(r, slot.a) });
    }
  };
  // Leftmost first; among equals the upper one.
  take("cam", groups.cam, (s, c) => s * 10 - c);
  // Rightmost first; among equals the upper one.
  take("btn", groups.btn, (s, c) => -s * 10 - c);
  // Whatever is left, bottom first (the inner ring under the badge reads well).
  take("drone", groups.drone, (_s, c) => c);
  return out;
}

/** Leader line and label anchor for a placed node (mockup geometry). */
export function callout(angle: number, r: number, compact: boolean) {
  const right = Math.sin((angle * Math.PI) / 180) >= -1e-9;
  const elbow = polar(r + 34, angle);
  const ex = elbow.x + (right ? 1 : -1) * (compact ? 8 : 46);
  const tx = ex + (right ? 6 : -6);
  return { right, lx: elbow.x, ly: elbow.y, ex, tx, anchor: right ? ("start" as const) : ("end" as const) };
}
